'use client'

import * as Sentry from '@sentry/nextjs'
import { useEffect, useRef } from 'react'
import { AlertTriangle, Home, RefreshCw } from 'lucide-react'

export default function GlobalError({
  error,
  reset,
}: {
  error: Error & { digest?: string }
  reset: () => void
}) {
  // Track if we've already reported this error to avoid duplicate reports
  const reportedRef = useRef(false)

  useEffect(() => {
    // Only report once per error instance
    if (reportedRef.current) return
    reportedRef.current = true

    // Log the error to Sentry with isolated scope to prevent tag pollution
    Sentry.withScope((scope) => {
      scope.setTag('errorBoundary', 'global')
      if (error.digest) {
        scope.setExtra('digest', error.digest)
      }
      Sentry.captureException(error)
    })

    // Cleanup: reset the reported flag when error changes
    return () => {
      reportedRef.current = false
    }
  }, [error])

  // Global error boundary must render its own html/body since the root layout may have errored
  // Using inline styles to match the design system without depending on Tailwind CSS
  return (
    <html lang="en">
      <body style={{ margin: 0, fontFamily: 'system-ui, -apple-system, sans-serif' }}>
        <div
          style={{
            minHeight: '100vh',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '24px',
            backgroundColor: 'hsl(0 0% 98%)',
          }}
        >
          <div
            style={{
              maxWidth: '448px',
              width: '100%',
              padding: '32px 24px',
              backgroundColor: 'white',
              borderRadius: '12px',
              boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1), 0 2px 4px -2px rgb(0 0 0 / 0.1)',
              textAlign: 'center',
            }}
          >
            <div style={{ marginBottom: '24px' }}>
              <div
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  width: '64px',
                  height: '64px',
                  borderRadius: '50%',
                  backgroundColor: 'hsl(0 84% 60% / 0.1)',
                  marginBottom: '16px',
                }}
              >
                <AlertTriangle
                  style={{ height: '32px', width: '32px', color: 'hsl(0 84% 60%)' }}
                  aria-hidden="true"
                />
              </div>
              <h1
                style={{
                  fontSize: '24px',
                  fontWeight: 700,
                  color: 'hsl(0 0% 9%)',
                  marginBottom: '8px',
                  margin: '0 0 8px 0',
                }}
              >
                Oops! Something went wrong
              </h1>
              <p style={{ color: 'hsl(0 0% 45%)', margin: 0 }}>
                We encountered an unexpected error. Don&apos;t worry, our team has been notified.
              </p>
            </div>

            {error.digest && (
              <p
                style={{
                  fontSize: '12px',
                  color: 'hsl(0 0% 45%)',
                  marginBottom: '24px',
                }}
              >
                Reference: {error.digest}
              </p>
            )}

            <div
              style={{
                display: 'flex',
                flexDirection: 'column',
                gap: '12px',
              }}
            >
              <button
                onClick={reset}
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '8px',
                  width: '100%',
                  padding: '10px 16px',
                  backgroundColor: 'hsl(142 76% 36%)',
                  color: 'white',
                  border: 'none',
                  borderRadius: '6px',
                  fontSize: '14px',
                  fontWeight: 500,
                  cursor: 'pointer',
                  transition: 'background-color 150ms',
                }}
                onMouseOver={(e) => e.currentTarget.style.backgroundColor = 'hsl(142 76% 30%)'}
                onMouseOut={(e) => e.currentTarget.style.backgroundColor = 'hsl(142 76% 36%)'}
                aria-label="Try loading the page again"
              >
                <RefreshCw style={{ height: '16px', width: '16px' }} aria-hidden="true" />
                Try again
              </button>
              <button
                onClick={() => window.location.href = '/'}
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '8px',
                  width: '100%',
                  padding: '10px 16px',
                  backgroundColor: 'transparent',
                  color: 'hsl(0 0% 25%)',
                  border: '1px solid hsl(0 0% 80%)',
                  borderRadius: '6px',
                  fontSize: '14px',
                  fontWeight: 500,
                  cursor: 'pointer',
                  transition: 'background-color 150ms',
                }}
                onMouseOver={(e) => e.currentTarget.style.backgroundColor = 'hsl(0 0% 96%)'}
                onMouseOut={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                aria-label="Go to home page"
              >
                <Home style={{ height: '16px', width: '16px' }} aria-hidden="true" />
                Go home
              </button>
            </div>

            <p
              style={{
                marginTop: '32px',
                fontSize: '14px',
                color: 'hsl(0 0% 45%)',
              }}
            >
              If this problem persists, please contact support.
            </p>
          </div>
        </div>
      </body>
    </html>
  )
}
